import AgentDomain from "entities/domain/agents/agent-domain";
import type ConversationDomain from "entities/domain/conversations/conversation-domain";

export enum UserPermissionsKey {
  MANAGE_ALL_CONVERSATIONS = "manage:all_conversations",
  MANAGE_UNASSIGNED_INBOX = "manage:unassigned_inbox",
  IMPORT_CONTACTS = "import:contacts",
  DELETE_CONTACTS = "delete:contacts",
  VIEW_PAYMENTS = "view:payments",
  SEND_PAYMENTS = "send:payments",
  MANAGE_INTEGRATIONS = "manage:integrations",
  MANAGE_AUTOMATIONS = "manage:automations",
  MANAGE_CAMPAIGNS = "manage:campaigns",
  MANAGE_TEAMMATES = "manage:teammates",
  MANAGE_ROLE_PERMISSIONS = "manage:role_permissions",
  MANAGE_ACCOUNT = "manage:account",
  MANAGE_TEMPLATES = "manage:templates",
  FUZEY_SUPER_USER = "super_user",
  VIEW_AGENTS_ANALYTICS = "view:agents_analytics",
  VIEW_AFTERSALES = "view:aftersales",
  DELETE_FILES = "delete:files",
  MANAGE_AI_ASSISTANTS = "manage:ai_assistants",
  INVOKE_AI_AGENT = "invoke:ai_agent",
  // "Manage Inboxes": fuzey-api PermissionName.MANAGE_INBOXES. Its migration
  // (0105) grants it to every role that holds manage:teammates.
  MANAGE_INBOXES = "manage:inboxes",
}

// Internal-beta rollout: only these staff agents see AI chats in the UI, and
// only when they also hold the invoke:ai_agent permission (which the API
// enforces on every AI thread endpoint). Super users always have access.
// Widen the rollout by adding agent ids here; general availability = drop the
// allowlist check in canAccessAiChats and keep the permission check.
export const AI_CHATS_ENABLED_AGENT_IDS: number[] = [
  3323, 2459, 3026,
  // Al Jenaibi (BMW Oman) + Euro Motors Bahrain rollout, 2026-07-14
  3549, 3548, 1952,
  // John Grose rollout, 2026-07-16
  1582, 1443, 1555, 1677,
];

export type UserPermissions = UserPermissionsKey[];

export function atLeastOnePermissionExists(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined,
  permissionKeys: UserPermissionsKey[]
) {
  // FREO: These helpers are intentionally null-safe; a missing agent implies no permissions.
  if (!currentAgent) {
    return false;
  }
  const permissions = currentAgent.getPermissions(merchantId);
  if (!permissions || permissions.length === 0) {
    return false;
  }

  for (const key of permissionKeys) {
    if (permissions.includes(key)) {
      return true;
    }
  }

  return false;
}

export function allPermissionsExist(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined,
  permissionKeys: UserPermissionsKey[]
) {
  // FREO: These helpers are intentionally null-safe; a missing agent implies no permissions.
  if (!currentAgent) {
    return false;
  }
  const permissions = currentAgent.getPermissions(merchantId);

  if (!permissions || permissions.length === 0) {
    return false;
  }

  for (const key of permissionKeys) {
    if (!permissions.includes(key)) {
      return false;
    }
  }

  return true;
}

export function canViewPayments(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.VIEW_PAYMENTS,
  ]);
}

export function canSendPayments(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.SEND_PAYMENTS,
  ]);
}

export function canManageTeamInbox(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_ALL_CONVERSATIONS,
  ]);
}

export function canManageUnassignedInbox(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_UNASSIGNED_INBOX,
  ]);
}

// Mirrors the conversation PATCH ownership gate; Inbox membership grants no
// additional access and does not take away an adviser's own conversations.
export function canUpdateConversation(
  conversation: ConversationDomain,
  currentAgent: AgentDomain | null | undefined
): boolean {
  if (
    !currentAgent?.merchants.some(
      (merchant) => merchant.id === conversation.merchantId && merchant.isActive
    )
  )
    return false;
  if (
    conversation.assignedAgentId === currentAgent.id ||
    isFuzeySuperUser(currentAgent)
  )
    return true;
  return conversation.assignedAgentId === null
    ? canManageUnassignedInbox(conversation.merchantId, currentAgent)
    : canManageTeamInbox(conversation.merchantId, currentAgent);
}

export function canImportContacts(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.IMPORT_CONTACTS,
  ]);
}

export function canManageAccount(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_ACCOUNT,
  ]);
}

export function canDeleteContacts(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.DELETE_CONTACTS,
  ]);
}

export function canManageIntegrations(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_INTEGRATIONS,
  ]);
}

export function canManageAutomations(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_AUTOMATIONS,
  ]);
}

export function canManageAiAssistants(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_AI_ASSISTANTS,
  ]);
}

export function canViewAgentsAnalytics(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.VIEW_AGENTS_ANALYTICS,
  ]);
}

export function canManageCampaigns(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_CAMPAIGNS,
  ]);
}

// Super-users always see aftersales (mirrors the backend permission-check bypass);
// everyone else needs the explicit view:aftersales permission, granted to admins by default.
export function canViewAftersales(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return (
    isFuzeySuperUser(currentAgent) ||
    allPermissionsExist(merchantId, currentAgent, [
      UserPermissionsKey.VIEW_AFTERSALES,
    ])
  );
}

export function canManageTeamMembers(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_TEAMMATES,
  ]);
}

export function canDeleteFiles(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.DELETE_FILES,
  ]);
}

export function canManageTemplates(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_TEMPLATES,
  ]);
}

// Only the dedicated capability: manage:teammates and manage:all_conversations
// are not a fallback, and the API enforces the same capability on every Inbox
// write.
export function canManageInboxes(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_INBOXES,
  ]);
}

export function canManageRolePermissions(
  merchantId: number,
  currentAgent: AgentDomain | null | undefined
) {
  return allPermissionsExist(merchantId, currentAgent, [
    UserPermissionsKey.MANAGE_ROLE_PERMISSIONS,
  ]);
}

export function isFuzeySuperUser(currentAgent: AgentDomain | null | undefined) {
  if (!currentAgent) {
    return false;
  }
  return currentAgent.merchants.some((merchant) =>
    allPermissionsExist(merchant.id, currentAgent, [
      UserPermissionsKey.FUZEY_SUPER_USER,
    ])
  );
}

export function canAccessAiChats(currentAgent: AgentDomain | null | undefined) {
  if (!currentAgent) {
    return false;
  }
  if (isFuzeySuperUser(currentAgent)) {
    return true;
  }
  if (!AI_CHATS_ENABLED_AGENT_IDS.includes(currentAgent.id)) {
    return false;
  }
  return currentAgent.merchants.some((merchant) =>
    allPermissionsExist(merchant.id, currentAgent, [
      UserPermissionsKey.INVOKE_AI_AGENT,
    ])
  );
}
